// src/utils/salesModel.js
import { format, parseISO, isValid } from 'date-fns'

// Payment status configurations
export const PAYMENT_STATUS = {
  PAID: 'paid',
  UNPAID: 'unpaid',
  PARTIAL: 'partial',
  REFUNDED: 'refunded',
  FAILED: 'failed'
}

// Order type configurations
export const ORDER_TYPE = {
  DINE_IN: 'dine-in',
  TAKEAWAY: 'takeaway',
  DELIVERY: 'delivery',
  ONLINE: 'online'
}

// Payment method configurations
export const PAYMENT_METHOD = {
  CASH: 'cash',
  CARD: 'card',
  DIGITAL: 'digital',
  BANK_TRANSFER: 'bank_transfer',
  MOBILE: 'mobile'
}

export class SalesModel {
  constructor(data = {}) {
    this.id = data._id || data.id || ''
    this.orderNumber = data.orderNumber || data.order_number || ''
    this.orderDate = data.orderDate || data.order_date || data.createdAt || ''
    this.customer = data.customer || {}
    this.branch = data.branch || {}
    this.orderType = data.orderType || data.order_type || ORDER_TYPE.DINE_IN
    this.items = data.orderItems || data.items || []
    
    // Handle the complex payment structure from the API
    this.subtotal = this.calculateSubtotal(this.items)
    this.tax = data.fees?.serviceFeeAmount || data.tax || 0
    this.discount = data.orderPromotionEngineResult?.orderDetails?.totalDiscount || data.discount || 0
    this.total = data.orderPayment?.totalAmount || data.total || data.totalAmount || 0
    
    this.paymentStatus = data.paymentStatus || data.payment_status || PAYMENT_STATUS.UNPAID
    this.paymentMethod = this.getPaymentMethodFromPayments(data.orderPayment?.paymentMethods) || PAYMENT_METHOD.CASH
    this.paymentDetails = data.orderPayment || data.payment_details || {}
    this.status = data.orderStatus || data.status || 'completed'
    this.notes = data.notes || ''
    this.createdBy = data.createdBy || data.created_by || {}
    this.updatedAt = data.updatedAt || data.updated_at || ''
    this.deliveryDetails = data.deliveryDetails || data.delivery_details || {}
    
    // Additional fields from API
    this.fees = data.fees || {}
    this.promotionResult = data.orderPromotionEngineResult || {}
  }

  // Calculate subtotal from order items
  calculateSubtotal(items) {
    if (!Array.isArray(items)) return 0
    
    return items.reduce((total, item) => {
      const unitPrice = item.unitPrice || item.price || 0
      const quantity = item.quantity || 0
      return total + (unitPrice * quantity)
    }, 0)
  }

  // Extract payment method from payment methods array
  getPaymentMethodFromPayments(paymentMethods) {
    if (!Array.isArray(paymentMethods) || paymentMethods.length === 0) {
      return PAYMENT_METHOD.CASH
    }

    // Get the primary payment method (first one or largest amount)
    const primaryPayment = paymentMethods.reduce((prev, current) => {
      return (current.amount || 0) > (prev.amount || 0) ? current : prev
    })

    return primaryPayment.method || PAYMENT_METHOD.CASH
  }

  // Format sale for display
  static formatSaleForDisplay(sale) {
    const saleModel = new SalesModel(sale)
    
    return {
      ...saleModel,
      orderDateFormatted: SalesModel.formatDate(saleModel.orderDate),
      orderTimeFormatted: SalesModel.formatTime(saleModel.orderDate),
      totalFormatted: SalesModel.formatCurrency(saleModel.total),
      subtotalFormatted: SalesModel.formatCurrency(saleModel.subtotal),
      taxFormatted: SalesModel.formatCurrency(saleModel.tax),
      discountFormatted: SalesModel.formatCurrency(saleModel.discount),
      itemsCount: saleModel.items.length,
      customerName: saleModel.customer?.name || saleModel.customer?.fullName || 'Walk-in Customer',
      customerMobile: saleModel.customer?.mobileNumber || saleModel.customer?.mobile || '',
      branchName: saleModel.branch?.name || 'Unknown Branch',
      paymentStatusConfig: SalesModel.getPaymentStatusConfig(saleModel.paymentStatus),
      orderTypeConfig: SalesModel.getOrderTypeConfig(saleModel.orderType),
      paymentMethodConfig: SalesModel.getPaymentMethodConfig(saleModel.paymentMethod)
    }
  }

  // Get payment status configuration
  static getPaymentStatusConfig(status) {
    const configs = {
      [PAYMENT_STATUS.PAID]: {
        label: 'Paid',
        color: 'bg-green-100 text-green-800',
        icon: '✅',
        badgeClass: 'bg-green-100 text-green-800'
      },
      [PAYMENT_STATUS.UNPAID]: {
        label: 'Unpaid',
        color: 'bg-red-100 text-red-800',
        icon: '❌',
        badgeClass: 'bg-red-100 text-red-800'
      },
      [PAYMENT_STATUS.PARTIAL]: {
        label: 'Partial',
        color: 'bg-yellow-100 text-yellow-800',
        icon: '⚠️',
        badgeClass: 'bg-yellow-100 text-yellow-800'
      },
      [PAYMENT_STATUS.REFUNDED]: {
        label: 'Refunded',
        color: 'bg-gray-100 text-gray-800',
        icon: '🔄',
        badgeClass: 'bg-gray-100 text-gray-800'
      },
      [PAYMENT_STATUS.FAILED]: {
        label: 'Failed',
        color: 'bg-red-100 text-red-800',
        icon: '💥',
        badgeClass: 'bg-red-100 text-red-800'
      }
    }

    return configs[status] || configs[PAYMENT_STATUS.UNPAID]
  }

  // Get order type configuration
  static getOrderTypeConfig(orderType) {
    const configs = {
      [ORDER_TYPE.DINE_IN]: {
        label: 'Dine-in',
        color: 'bg-blue-100 text-blue-800',
        icon: '🍽️',
        badgeClass: 'bg-blue-100 text-blue-800'
      },
      [ORDER_TYPE.TAKEAWAY]: {
        label: 'Takeaway',
        color: 'bg-orange-100 text-orange-800',
        icon: '🥡',
        badgeClass: 'bg-orange-100 text-orange-800'
      },
      [ORDER_TYPE.DELIVERY]: {
        label: 'Delivery',
        color: 'bg-purple-100 text-purple-800',
        icon: '🚚',
        badgeClass: 'bg-purple-100 text-purple-800'
      },
      [ORDER_TYPE.ONLINE]: {
        label: 'Online',
        color: 'bg-green-100 text-green-800',
        icon: '🌐',
        badgeClass: 'bg-green-100 text-green-800'
      }
      
    }

    return configs[orderType] || configs[ORDER_TYPE.DINE_IN]
  }

  // Get payment method configuration
  static getPaymentMethodConfig(paymentMethod) {
    const configs = {
      [PAYMENT_METHOD.CASH]: {
        label: 'Cash',
        color: 'bg-green-100 text-green-800',
        icon: '💵',
        badgeClass: 'bg-green-100 text-green-800'
      },
      [PAYMENT_METHOD.CARD]: {
        label: 'Card',
        color: 'bg-blue-100 text-blue-800',
        icon: '💳',
        badgeClass: 'bg-blue-100 text-blue-800'
      },
      [PAYMENT_METHOD.DIGITAL]: {
        label: 'Digital',
        color: 'bg-purple-100 text-purple-800',
        icon: '📱',
        badgeClass: 'bg-purple-100 text-purple-800'
      },
      [PAYMENT_METHOD.BANK_TRANSFER]: {
        label: 'Bank Transfer',
        color: 'bg-indigo-100 text-indigo-800',
        icon: '🏦',
        badgeClass: 'bg-indigo-100 text-indigo-800'
      },
      [PAYMENT_METHOD.MOBILE]: {
        label: 'Mobile Payment',
        color: 'bg-teal-100 text-teal-800',
        icon: '📲',
        badgeClass: 'bg-teal-100 text-teal-800'
      }
    }

    return configs[paymentMethod] || configs[PAYMENT_METHOD.CASH]
  }

  // Format date
  static formatDate(dateString) {
    if (!dateString) return '-'
    
    try {
      const date = typeof dateString === 'string' ? parseISO(dateString) : new Date(dateString)
      if (!isValid(date)) return '-'
      return format(date, 'MMM dd, yyyy')
    } catch (error) {
      console.error('Date formatting error:', error)
      return '-'
    }
  }

  // Format time
  static formatTime(dateString) {
    if (!dateString) return '-'
    
    try {
      const date = typeof dateString === 'string' ? parseISO(dateString) : new Date(dateString)
      if (!isValid(date)) return '-'
      return format(date, 'HH:mm')
    } catch (error) {
      console.error('Time formatting error:', error)
      return '-'
    }
  }

  // Format currency
  static formatCurrency(amount, currency = 'LKR') {
    if (typeof amount !== 'number') {
      amount = parseFloat(amount) || 0
    }

    try {
      // Use simple Rs. format for Sri Lankan Rupees
      if (currency === 'LKR') {
        return `Rs.${amount.toFixed(2)}`
      }
      
      return new Intl.NumberFormat('en-LK', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount)
    } catch {
      // Fallback formatting
      return `Rs.${amount.toFixed(2)}`
    }
  }

  // Calculate total items quantity
  static getTotalItemsQuantity(items) {
    if (!Array.isArray(items)) return 0
    
    return items.reduce((total, item) => {
      return total + (parseInt(item.quantity) || 0)
    }, 0)
  }

  // Get items summary
  static getItemsSummary(items, maxItems = 3) {
    if (!Array.isArray(items) || items.length === 0) {
      return 'No items'
    }

    const itemNames = items
      .slice(0, maxItems)
      .map(item => item.productName || item.name || 'Unknown Item')
      .join(', ')

    const remainingCount = items.length - maxItems
    return remainingCount > 0 
      ? `${itemNames} +${remainingCount} more`
      : itemNames
  }

  // Validate sale data
  static validateSaleData(saleData) {
    const errors = []

    if (!saleData.orderNumber) {
      errors.push('Order number is required')
    }

    if (!saleData.customer) {
      errors.push('Customer information is required')
    }

    if (!Array.isArray(saleData.items) || saleData.items.length === 0) {
      errors.push('At least one item is required')
    }

    if (typeof saleData.total !== 'number' || saleData.total <= 0) {
      errors.push('Valid total amount is required')
    }

    return {
      isValid: errors.length === 0,
      errors
    }
  }

  // Create new sale instance
  static createSale(saleData) {
    const validation = SalesModel.validateSaleData(saleData)
    
    if (!validation.isValid) {
      throw new Error(`Invalid sale data: ${validation.errors.join(', ')}`)
    }

    return new SalesModel(saleData)
  }

  // Convert to API format
  toApiFormat() {
    return {
      orderNumber: this.orderNumber,
      orderDate: this.orderDate,
      customer: this.customer,
      branch: this.branch,
      orderType: this.orderType,
      items: this.items,
      subtotal: this.subtotal,
      tax: this.tax,
      discount: this.discount,
      total: this.total,
      paymentStatus: this.paymentStatus,
      paymentMethod: this.paymentMethod,
      paymentDetails: this.paymentDetails,
      notes: this.notes,
      deliveryDetails: this.deliveryDetails
    }
  }

  // Get display summary
  getDisplaySummary() {
    return {
      id: this.id,
      orderNumber: this.orderNumber,
      date: SalesModel.formatDate(this.orderDate),
      time: SalesModel.formatTime(this.orderDate),
      customer: this.customer?.name || 'Walk-in Customer',
      total: SalesModel.formatCurrency(this.total),
      itemsCount: this.items.length,
      paymentStatus: this.paymentStatus,
      orderType: this.orderType
    }
  }
}